import { FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@material-ui/core';
import { format } from 'date-fns';
import i18n from 'i18n';
import TooltipWrap from 'components/TooltipWrap';
import { findTaskByGuid, LnsTaskToFormData } from 'pages/assignments/utils';
import PriorityTag from 'pages/assignments/components/PriorityTag';
import ActionButtons from 'pages/assignments/components/AssignmentList/ActionButtons';
import colors from 'styles/colors';
import TableBuilder from 'components/Table';
import StatusIcon from 'components/StatusIcon';
import Badge, { BadgeColour } from 'components/Badge';
import { useSelector } from 'react-redux';

interface ListAssignmentsI {
  isAdmin: boolean;
  currentLnsUser: $Lns.User;
  assignments: $Lns.Task[];
  onViewAssignment: (assignment: $Lns.Task) => void;
  onEditAssignment: (assignment: $Lns.Task) => void;
  onDeleteAssignment: (assignmentGuid: string) => void;
}

const ListAssignments: FunctionComponent<ListAssignmentsI> = ({
  isAdmin,
  assignments,
  currentLnsUser,
  onViewAssignment,
  onEditAssignment,
  onDeleteAssignment
}: ListAssignmentsI) => {
  const { taskStates } = useSelector(
    (state: $Lns.DefaultState) => state.default
  );

  const onHandleView = (guid: string) => {
    const assignment = findTaskByGuid(guid, assignments);
    if (assignment) onViewAssignment(assignment);
  };

  const onHandleEdit = (guid: string) => {
    const assignment = findTaskByGuid(guid, assignments);
    if (assignment) onEditAssignment(assignment);
  };

  const onHandleDelete = (guid: string) => {
    const assignment = findTaskByGuid(guid, assignments);
    if (assignment) onDeleteAssignment(assignment.guid);
  };

  const buildActionBtns = (assignment: $Lns.Task) => {
    const currentCellAssignment = findTaskByGuid(assignment.guid, assignments);

    if (!currentCellAssignment) return <p>{i18n.t('data.error')}</p>;

    return (
      <ActionButtons
        isAdmin={isAdmin}
        assignment={LnsTaskToFormData(currentCellAssignment)}
        currentLnsUser={currentLnsUser}
        onHandleView={onHandleView}
        onHandleEdit={onHandleEdit}
        onHandleDelete={onHandleDelete}
      />
    );
  };

  const getTaskStateName = (guid: string) => {
    const taskState = taskStates.find(task => task.guid === guid);
    return taskState ? taskState.name : '';
  };

  const stateColor = (state: string): BadgeColour => {
    switch (state) {
      case 'new':
        return 'blue';
      case 'pending assignment':
        return 'orange';
      case 'assigned':
      case 'in progress':
      case 'completed':
        return 'green';
      case 'rejected':
        return 'darkRed';
      default:
        return 'gray';
    }
  };

  const cells = assignments.map(assignment => {
    return {
      fields: {
        priority: { value: <PriorityTag priority={assignment.priority} /> },
        story: {
          value:
            assignment.story instanceof Object && assignment.story.guid ? (
              <Link
                to={`/my-lns/${assignment.story.guid}/edit`}
                style={{ color: colors.brand[600], textDecoration: 'none' }}
              >
                {assignment.story.slug}
              </Link>
            ) : (
              'Not Set'
            )
        },
        title: { value: assignment.title.toUpperCase() },
        dueDate: {
          value: (
            <Box display="flex" whiteSpace="break-spaces">
              {new Date(assignment.dateDue) < new Date() && (
                <Box paddingRight={1}>
                  <TooltipWrap
                    title={i18n.t('pages.assignments.tooltip.overdue')}
                  >
                    <StatusIcon status="warning" />
                  </TooltipWrap>
                </Box>
              )}
              <TooltipWrap title={format(new Date(assignment.dateDue), 'Pp')}>
                {format(new Date(assignment.dateDue), 'Pp')}
              </TooltipWrap>
            </Box>
          )
        },
        taskState: {
          value: (
            <Badge
              label={
                assignment.taskState instanceof Object
                  ? getTaskStateName(assignment.taskState.guid)
                  : 'N/A'
              }
              color={
                assignment.taskState instanceof Object
                  ? stateColor(assignment.taskState.name)
                  : undefined
              }
              variant="rounded"
              capitalize
            />
          )
        },
        assignedTo: {
          value:
            assignment.assignedTo instanceof Object
              ? assignment.assignedTo.username
              : 'Not Set'
        },
        actions: { value: buildActionBtns(assignment) }
      }
    };
  });

  const rows = [
    i18n.t('pages.assignments.formLabel.priority'),
    i18n.t('pages.assignments.formLabel.storySlug'),
    i18n.t('pages.assignments.formLabel.title'),
    i18n.t('pages.assignments.formLabel.dateDue'),
    i18n.t('pages.assignments.formLabel.taskStates'),
    'Assignee',
    '' // Actions
  ];

  return <TableBuilder rows={rows} cells={cells} isDraggable />;
};

export default ListAssignments;
